import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("tny_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// PROMPT 49 (estabilidad): mientras el backend reinicia (deploy, o el poller de
// Bókun/correo corriendo), las requests pueden fallar con error de red o con un
// 5xx/520 de Cloudflare aunque la sesión siga siendo válida. En vez de mostrarle
// al usuario el error crudo, reintentamos automáticamente (solo GET, que es
// seguro repetir) con backoff, y avisamos vía evento a un banner global
// mientras dura. Los POST/PUT/DELETE no se reintentan solos (podrían duplicar
// una acción) -- ahí se le muestra el error para que decida reintentar.
const REINTENTOS = 4;
const ESPERA_BASE_MS = 1200;

function esErrorTransitorio(error) {
  if (!error.response) return true; // sin respuesta = caída/timeout de red
  return [502, 503, 504, 520, 521, 522, 523, 524].includes(error.response.status);
}

function avisarEstado(caido) {
  window.dispatchEvent(new CustomEvent(caido ? "tny:backend-down" : "tny:backend-up"));
}

// Watchdog: una vez que se detecta la caída, se sigue chequeando /health en
// segundo plano (fuera del interceptor, para no re-entrar en el retry) hasta
// que el backend vuelva a responder. Antes el banner se apagaba solo al
// agotar los 4 reintentos de UNA request aunque el backend siguiera caído --
// esto asegura que el aviso refleje el estado real hasta que se confirma la
// recuperación.
let watchdogActivo = false;
function iniciarWatchdog() {
  if (watchdogActivo) return;
  watchdogActivo = true;
  const intentar = () => {
    axios
      .get(`${API}/health`, { timeout: 4000 })
      .then(() => {
        watchdogActivo = false;
        avisarEstado(false);
      })
      .catch(() => setTimeout(intentar, 4000));
  };
  setTimeout(intentar, 4000);
}

api.interceptors.response.use(
  (response) => {
    avisarEstado(false);
    return response;
  },
  async (error) => {
    const config = error.config || {};
    if (!esErrorTransitorio(error) || config.method?.toLowerCase() !== "get") {
      return Promise.reject(error);
    }
    config.__reintento = (config.__reintento || 0) + 1;
    avisarEstado(true);
    if (config.__reintento > REINTENTOS) {
      // Se agotaron los reintentos de ESTA request, pero el backend puede
      // seguir caído -- el watchdog sigue avisando hasta confirmar que
      // realmente volvió (nunca se declara "arriba" por descarte).
      iniciarWatchdog();
      return Promise.reject(error);
    }
    await new Promise((r) => setTimeout(r, ESPERA_BASE_MS * config.__reintento));
    return api(config);
  }
);

export function formatApiError(detail) {  if (detail == null) return "Ocurrió un error. Intentá de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export async function downloadFile(path, filename, params) {
  const res = await api.get(path, { params, responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export default api;
